import { StrictMode, Component } from "react";
import { createRoot } from "react-dom/client";
import { validateEnv } from "./utils/env.js";
import { initMonitoring, captureError } from "./lib/monitoring.js";
import App from "./App.jsx";
import { ToastProvider } from "./components/Toast.jsx";

// Validate env vars before rendering — throws on missing required vars,
// which the ErrorBoundary will catch and display as a readable message.
validateEnv();

// Install global error/rejection handlers (no-op until VITE_SENTRY_DSN is set).
initMonitoring();

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    captureError(error, { componentStack: info.componentStack });
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: "2rem",
          textAlign: "center",
          fontFamily: "Inter, -apple-system, sans-serif",
          background: "var(--bg, #F5F4F0)",
          color: "var(--text, #1C1C1C)",
          gap: "16px",
        }}>
          <div style={{ fontSize: 32 }}>⚠️</div>
          <h2 style={{ fontWeight: 400, fontSize: 20, margin: 0 }}>Something went wrong</h2>
          <p style={{ color: "var(--text2, #8A8A86)", fontSize: 13, maxWidth: 280, margin: 0 }}>
            {this.state.error.message || "An unexpected error occurred."}
          </p>
          <button
            onClick={() => this.setState({ error: null })}
            style={{
              marginTop: 8,
              padding: "12px 24px",
              borderRadius: 12,
              border: "none",
              background: "var(--accent, #7EB8A4)",
              color: "#fff",
              fontSize: 13,
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <ErrorBoundary>
      <ToastProvider>
        <App />
      </ToastProvider>
    </ErrorBoundary>
  </StrictMode>
);

// Register service worker for PWA offline support
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { updateViaCache: "none" })
      .catch((e) => console.error("Service worker registration failed:", e));
  });
}
